import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import axios from 'axios';
import { Camera, X, ShoppingCart, Trash2, Plus, Minus, CreditCard, Banknote, QrCode, AlertCircle, Check, Copy, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.QR_CODE,
];

export const POSScanner = ({ token, onSaleComplete, products: externalProducts }) => {
  const [scanning, setScanning] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [showPixPayment, setShowPixPayment] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [copied, setCopied] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const html5QrCodeRef = useRef(null);

  // Use external products if provided, otherwise load from API
  useEffect(() => {
    if (externalProducts && externalProducts.length > 0) {
      setAllProducts(externalProducts.filter(p => p.stock > 0));
    } else {
      loadProducts();
    }
  }, [token, externalProducts]);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllProducts(response.data.filter(p => p.stock > 0));
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const filteredProducts = allProducts.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.barcode?.includes(searchTerm) ||
    p.id?.includes(searchTerm)
  ).slice(0, 10); // Limit to 10 results for performance

  const startScanner = async () => {
    setCameraError('');
    setScanning(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      html5QrCodeRef.current = new Html5Qrcode("pos-scanner-container", {
        formatsToSupport: BARCODE_FORMATS,
        verbose: false
      });
      
      const config = {
        fps: 15,
        qrbox: { width: 280, height: 100 },
        aspectRatio: 1.777778,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true }
      };

      try {
        await html5QrCodeRef.current.start(
          { facingMode: { exact: "environment" } },
          config,
          (decodedText) => handleScan(decodedText),
          () => {}
        );
      } catch {
        await html5QrCodeRef.current.start(
          { facingMode: "environment" },
          config,
          (decodedText) => handleScan(decodedText),
          () => {}
        );
      }
    } catch (err) {
      setCameraError('Erro câmera. Use busca manual.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.stop(); } catch {}
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleScan = async (code) => {
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    
    try {
      const response = await axios.get(`${API}/admin/products/barcode/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      addToCart(response.data);
      toast.success(`${response.data.name} adicionado!`);
    } catch {
      toast.error('Produto não encontrado no inventário');
    }
  };

  const handleManualSearch = async () => {
    if (manualCode.trim()) {
      await handleScan(manualCode.trim());
      setManualCode('');
    }
  };

  const addToCart = (product) => {
    if (product.stock <= 0) {
      toast.error('Produto sem estoque');
      return;
    }

    setCartItems(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock) {
          toast.error('Estoque insuficiente');
          return prev;
        }
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        discount: product.discount || 0,
        stock: product.stock,
        barcode: product.barcode
      }];
    });
  };

  const addProductFromSearch = (product) => {
    addToCart(product);
    setShowProductSearch(false);
    setSearchTerm('');
    toast.success(`${product.name} adicionado!`);
  };

  const updateQuantity = (productId, delta) => {
    setCartItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > item.stock) {
          toast.error('Estoque insuficiente');
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeItem = (productId) => {
    setCartItems(prev => prev.filter(item => item.product_id !== productId));
  };

  const getSubtotal = () => cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const getTotalDiscount = () => cartItems.reduce((sum, item) => sum + (item.price * item.quantity * (item.discount / 100)), 0);
  const getTotal = () => getSubtotal() - getTotalDiscount();

  const handlePayment = async (method) => {
    if (cartItems.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }

    setLoading(true);
    try {
      const saleData = {
        items: cartItems.map(item => ({
          product_id: item.product_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          discount: item.discount
        })),
        subtotal: getSubtotal(),
        total_discount: getTotalDiscount(),
        total: getTotal(),
        payment_method: method,
        customer_name: customerName,
        notes: ''
      };

      const response = await axios.post(`${API}/admin/sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (method === 'pix' && response.data.pix_code) {
        setPixData(response.data);
        setShowPayment(false);
        setShowPixPayment(true);
      } else {
        toast.success(`Venda ${response.data.sale_number} finalizada!`);
        resetSale();
      }
      
      loadProducts(); // Reload products to update stock
      if (onSaleComplete) onSaleComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar venda');
    } finally {
      setLoading(false);
    }
  };

  const resetSale = () => {
    setCartItems([]);
    setCustomerName('');
    setShowPayment(false);
    setShowPixPayment(false);
    setPixData(null);
  };

  const copyPixCode = () => {
    if (pixData?.pix_code) {
      navigator.clipboard.writeText(pixData.pix_code);
      setCopied(true);
      toast.success('Código copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const confirmPixPayment = () => {
    toast.success(`Venda ${pixData?.sale_number} confirmada!`);
    resetSale();
  };

  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Scanner Section */}
      <div className="space-y-4">
        <div className="bg-card rounded-xl border p-4">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Adicionar Produto
          </h3>

          {cameraError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-yellow-800">{cameraError}</p>
            </div>
          )}

          {!scanning ? (
            <div className="space-y-3">
              <Button onClick={startScanner} className="w-full bg-green-600 hover:bg-green-700 py-5">
                <Camera className="w-5 h-5 mr-2" />
                Escanear Código
              </Button>
              
              <Button onClick={() => setShowProductSearch(true)} variant="outline" className="w-full py-5">
                <Search className="w-5 h-5 mr-2" />
                Buscar Produto
              </Button>

              <div className="flex gap-2">
                <Input
                  placeholder="Código manual"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleManualSearch()}
                  className="font-mono"
                />
                <Button onClick={handleManualSearch} variant="outline">OK</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div id="pos-scanner-container" className="w-full rounded-xl overflow-hidden bg-black" style={{ minHeight: '250px' }} />
              <Button onClick={stopScanner} variant="destructive" className="w-full">
                <X className="w-5 h-5 mr-2" />Fechar
              </Button>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Itens no carrinho</p>
          <p className="text-3xl font-black">{cartItems.reduce((sum, item) => sum + item.quantity, 0)}</p>
        </div>
      </div>

      {/* Cart Section */}
      <div className="bg-card rounded-xl border p-4 flex flex-col" style={{ minHeight: '450px' }}>
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" />
          Carrinho
        </h3>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-center p-4">
            <div>
              <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Escaneie ou busque produtos</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {cartItems.map(item => (
                <div key={item.product_id} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      R$ {item.price.toFixed(2)}
                      {item.discount > 0 && <span className="text-green-600 ml-1">-{item.discount}%</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQuantity(item.product_id, -1)}>
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="w-8 text-center font-bold">{item.quantity}</span>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQuantity(item.product_id, 1)}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="font-bold text-sm w-16 text-right">
                    R$ {(item.price * item.quantity * (1 - item.discount/100)).toFixed(2)}
                  </p>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeItem(item.product_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>R$ {getSubtotal().toFixed(2)}</span>
              </div>
              {getTotalDiscount() > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Desconto</span>
                  <span>- R$ {getTotalDiscount().toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-black">
                <span>Total</span>
                <span className="text-primary">R$ {getTotal().toFixed(2)}</span>
              </div>
              <Button onClick={() => setShowPayment(true)} className="w-full bg-primary hover:bg-primary/90 py-6 mt-4">
                Finalizar Venda
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Product Search Dialog */}
      <Dialog open={showProductSearch} onOpenChange={setShowProductSearch}>
        <DialogContent className="sm:max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Buscar Produto</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              placeholder="Nome, código ou ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {filteredProducts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhum produto encontrado</p>
            ) : (
              filteredProducts.map(product => (
                <button
                  key={product.id}
                  onClick={() => addProductFromSearch(product)}
                  className="w-full flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted text-left"
                >
                  <img src={product.image} alt="" className="w-12 h-12 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {product.barcode && <span className="font-mono">{product.barcode} • </span>}
                      Est: {product.stock}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">R$ {product.price.toFixed(2)}</p>
                    {product.discount > 0 && <p className="text-xs text-green-600">-{product.discount}%</p>}
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Finalizar Venda</DialogTitle>
            <DialogDescription>
              Total: <span className="text-2xl font-black text-primary">R$ {getTotal().toFixed(2)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Nome do cliente (opcional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="mb-4"
            />
            <p className="text-sm font-medium mb-3">Forma de Pagamento:</p>
            <div className="grid grid-cols-3 gap-3">
              <Button variant="outline" className="flex flex-col gap-2 h-24 hover:bg-green-50 hover:border-green-500" onClick={() => handlePayment('dinheiro')} disabled={loading}>
                <Banknote className="w-8 h-8 text-green-600" />
                <span className="text-xs">Dinheiro</span>
              </Button>
              <Button variant="outline" className="flex flex-col gap-2 h-24 hover:bg-blue-50 hover:border-blue-500" onClick={() => handlePayment('cartao')} disabled={loading}>
                <CreditCard className="w-8 h-8 text-blue-600" />
                <span className="text-xs">Cartão</span>
              </Button>
              <Button variant="outline" className="flex flex-col gap-2 h-24 hover:bg-teal-50 hover:border-teal-500" onClick={() => handlePayment('pix')} disabled={loading}>
                <QrCode className="w-8 h-8 text-teal-600" />
                <span className="text-xs">PIX</span>
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIX Payment Dialog */}
      <Dialog open={showPixPayment} onOpenChange={setShowPixPayment}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-teal-600" />
              Pagamento PIX
            </DialogTitle>
            <DialogDescription>
              Venda: <span className="font-bold">{pixData?.sale_number}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 text-center">
            <p className="text-3xl font-black text-primary mb-4">R$ {pixData?.total?.toFixed(2)}</p>
            
            {pixData?.pix_qrcode && (
              <div className="bg-white p-4 rounded-xl inline-block mb-4 border">
                <img src={`data:image/png;base64,${pixData.pix_qrcode}`} alt="QR Code PIX" className="w-48 h-48 mx-auto" />
              </div>
            )}
            
            <p className="text-sm text-muted-foreground mb-2">Escaneie o QR ou copie:</p>
            
            <div className="flex gap-2 mb-4">
              <Input value={pixData?.pix_code || ''} readOnly className="font-mono text-xs" />
              <Button variant="outline" onClick={copyPixCode}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              Após pagamento, clique em "Confirmar"
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={resetSale} className="w-full sm:w-auto">Cancelar</Button>
            <Button onClick={confirmPixPayment} className="w-full sm:w-auto bg-green-600 hover:bg-green-700">
              <Check className="w-4 h-4 mr-2" />Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
